import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { logout } from '../../App';
import Cookies from 'js-cookie';
import axiosInstance from '../../api/axiosInstance';
import { 
  HiOutlineHome, 
  HiOutlineCalendar, 
  HiOutlineUserGroup, 
  HiOutlineClock, 
  HiOutlineCash,
  HiOutlineBell,
  HiOutlineLogout,
  HiOutlineMenuAlt2,
  HiOutlineX,
  HiOutlineUserCircle,
  HiOutlineClipboardList,
  HiOutlineChevronDown
} from 'react-icons/hi';
import GenevixLogo from '../../assets/Doctor_Images/Genevix_Logo.png';

const ALL_MENU_ITEMS = [
  { name: 'Dashboard', icon: HiOutlineHome, path: '/dashboard', accessKey: null },
  { name: 'Appointments', icon: HiOutlineCalendar, path: '/dashboard/appointments', accessKey: 'appointment_access' },
  { name: 'Queue', icon: HiOutlineUserGroup, path: '/dashboard/queue', accessKey: null },
  { name: 'Services', icon: HiOutlineClipboardList, path: '/dashboard/services', accessKey: 'service_access' },
  { name: 'Working Hours', icon: HiOutlineClock, path: '/dashboard/workinghours', accessKey: 'workinghrs_access' },
  { name: 'Revenue', icon: HiOutlineCash, path: '/dashboard/revenue', accessKey: 'revenue_access' },
  { name: 'Notifications', icon: HiOutlineBell, path: '/dashboard/notification', accessKey: null },
  { name: 'Profile', icon: HiOutlineUserCircle, path: '/dashboard/profile', accessKey: null },
];

const MainLayout = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [clinicLogo, setClinicLogo] = useState(null);
  const [accessPermissions, setAccessPermissions] = useState(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    fetchClinicProfile();
  }, []);

  const fetchClinicProfile = async () => {
    try {
      const clinicId = Cookies.get('clinic_id') || Cookies.get('Clinic_id');
      if (!clinicId) return;

      const response = await axiosInstance.post('clinic/getClinicProfile', {
        clinic_id: clinicId
      });

      if (response.data.status === 1 && response.data.data?.[0]) {
        const data = response.data.data[0];
        setProfileData(data);
        const logo = data.profileImage || data.vendor_profile_path;
        if (logo && logo.trim() !== "") {
          setClinicLogo(logo);
        }

        // Check if access fields are returned directly in clinic profile
        if (data.appointment_access !== undefined || data.service_access !== undefined) {
          setAccessPermissions({
            appointment_access: data.appointment_access == 1,
            service_access: data.service_access == 1,
            workinghrs_access: data.workinghrs_access == 1,
            revenue_access: data.revenue_access == 1,
          });
        } else {
          // Fetch doctor profile to get access permissions
          try {
            const doctorRes = await axiosInstance.post('clinic/getClinicWiseDoctor', { clinic_id: clinicId });
            if (doctorRes.data.status === 1 && doctorRes.data.data?.[0]) {
              const doctorId = doctorRes.data.data[0].doctor_id;
              const profRes = await axiosInstance.post('doctor/getDoctorProfile', { doctor_id: doctorId, clinic_id: clinicId });
              if (profRes.data.status === 1 && profRes.data.data?.[0]) {
                const pd = profRes.data.data[0];
                setAccessPermissions({
                  appointment_access: pd.appointment_access == 1,
                  service_access: pd.service_access == 1,
                  workinghrs_access: pd.workinghrs_access == 1,
                  revenue_access: pd.revenue_access == 1,
                });
              }
            }
          } catch (e) {
            console.error('Failed to fetch doctor access permissions', e);
          }
        }
      }
    } catch (e) {
      console.error('Failed to fetch clinic profile data', e);
    }
  };

  // Filter menu items based on permissions; if permissions not loaded yet, show all
  const menuItems = ALL_MENU_ITEMS.filter(item => {
    if (!item.accessKey || !accessPermissions) return true;
    return accessPermissions[item.accessKey] === true;
  });

  const handleLogout = () => {
    logout(history);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 fixed h-full z-50 overflow-hidden`}
      >
        <div className="h-20 flex items-center justify-center px-4 border-b border-gray-100">
          {isSidebarOpen ? (
            <img src={clinicLogo || GenevixLogo} alt="Logo" className="h-14 w-auto object-contain py-2" />
          ) : (
            <div className="w-8 h-8 bg-teal-600 rounded flex items-center justify-center text-white font-bold">G</div>
          )}
        </div>

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.name}
                onClick={() => history.push(item.path)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-teal-50 text-teal-700 font-semibold' 
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                <item.icon size={22} className={`shrink-0 ${isActive ? 'text-teal-600' : ''}`} />
                {isSidebarOpen && <span className="text-sm">{item.name}</span>}
                {isActive && isSidebarOpen && <div className="ml-auto w-1.5 h-1.5 bg-teal-600 rounded-full" />}
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <button 
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 p-3 text-red-500 hover:bg-red-50 rounded-xl transition-all`}
          >
            <HiOutlineLogout size={22} />
            {isSidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Header */}
        <header className="h-20 bg-white border-b border-gray-200 flex items-center justify-between px-6 sm:px-8 sticky top-0 z-40">
          <button
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            {isSidebarOpen ? <HiOutlineX size={22} /> : <HiOutlineMenuAlt2 size={22} />}
          </button>

          <div className="flex items-center gap-4 sm:gap-6">
            <button
              onClick={() => history.push('/dashboard/notification')}
              className="relative p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
              title="Notifications"
            >
              <HiOutlineBell size={22} />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-teal-100 border-2 border-white shadow-sm flex items-center justify-center text-teal-700 font-bold overflow-hidden shrink-0">
                {clinicLogo ? (
                  <img src={clinicLogo} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  Cookies.get('doctor_Name')?.replace(/"/g, '').charAt(0) || 'C'
                )}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-bold text-gray-900 leading-none">
                  {Cookies.get('doctor_Name')?.replace(/"/g, '') || 'Clinic Admin'}
                </p>
                <p className="text-xs text-gray-500 mt-1.5">
                  {Cookies.get('clinic_name')?.replace(/"/g, '') || 'Main Clinic'}
                </p>
              </div>
              <HiOutlineChevronDown className="text-gray-400 hidden sm:block" size={16} />
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
